import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import AppLayout from './components/app-layout';
import { SECTIONS, showV2 } from './constants/routes';
import { IdentityProvider, RETURN_TO_KEY } from './features/identity/identity';
import { SignInGate } from './features/identity/sign-in-screen';
import CallPage from './pages/calls/call-page';
import CallsPage from './pages/calls/calls-page';
import CasinoPage from './pages/casinos/casino-page';
import CasinosPage from './pages/casinos/casinos-page';
import AllConversationsPage from './pages/conversations/all-page';
import ConversationsPage from './pages/conversations/conversations-page';
import EmailInboxPage from './pages/conversations/email-page';
import InstagramInboxPage from './pages/conversations/instagram-page';
import FollowUpsPage from './pages/follow-ups/follow-ups-page';
import AnswersPage from './pages/knowledge/answers-page';
import ArticleEditorPage from './pages/knowledge/article-editor-page';
import ArticlesPage from './pages/knowledge/articles-page';
import LeadPage from './pages/leads/lead-page';
import LeadsPage from './pages/leads/leads-page';
import SuggestionsPage from './pages/leads/suggestions-page';
import ComposePage from './pages/messaging/compose-page';
import MessagePage from './pages/messaging/message-page';
import MessagesPage from './pages/messaging/messages-page';
import MessagingPage from './pages/messaging/messaging-page';
import ReviewPage from './pages/messaging/review-page';
import TemplateEditorPage from './pages/messaging/template-editor-page';
import TemplatesPage from './pages/messaging/templates-page';
import TriggerEditorPage from './pages/messaging/trigger-editor-page';
import TriggersPage from './pages/messaging/triggers-page';
import OverviewPage from './pages/overview/overview-page';
import CampaignsReportPage from './pages/reports/campaigns-page';
import FunnelReportPage from './pages/reports/funnel-page';
import SourcesReportPage from './pages/reports/sources-page';
import SegmentEditorPage from './pages/segments/segment-editor-page';
import SegmentsPage from './pages/segments/segments-page';
import ChannelsSettingsPage from './pages/settings/channels-page';
import StaffPage from './pages/settings/staff-page';
import SyncSettingsPage from './pages/settings/sync-page';
import AssistantSettingsPage from './pages/settings/assistant-page';
import HostessPage from './pages/assistant/hostess-page';
import SitePreviewPage from './pages/assistant/site-preview-page';
import TelegramSettingsPage from './pages/settings/telegram-page';

/**
 * OIDC return (/auth/callback), copied from spark-board. SignInGate only renders it once the
 * sign-in is done, so it just goes back to the page asked for before the Zitadel redirect.
 */
function AuthCallback() {
  const navigate = useNavigate();
  useEffect(() => {
    let dest = SECTIONS[0].path;
    try {
      const rt = sessionStorage.getItem(RETURN_TO_KEY);
      sessionStorage.removeItem(RETURN_TO_KEY);
      // A path of this site only: "//host" or "/\host" would be another one.
      if (rt && /^\/(?![/\\])/.test(rt) && !rt.startsWith('/auth/callback')) dest = rt;
    } catch {
      /* ignore */
    }
    navigate(dest, { replace: true });
  }, [navigate]);
  return null;
}

/** Every page. Static segments (`/leads/suggestions`, `/messaging/messages/new`) rank above `:id`. */
const App = () => (
  <BrowserRouter>
    <IdentityProvider>
      <SignInGate>
        <Routes>
          <Route path="/auth/callback" element={<AuthCallback />} />
          {/* The website's contact page as a visitor sees it (ASSIST-1), outside Prisma's frame. */}
          <Route path="/site-preview" element={<SitePreviewPage />} />
          <Route element={<AppLayout />}>
            <Route index element={<Navigate to={SECTIONS[0].path} replace />} />
            <Route path="/overview" element={<OverviewPage />} />

            <Route path="/leads" element={<LeadsPage />} />
            <Route path="/leads/suggestions" element={<SuggestionsPage />} />
            <Route path="/leads/:id" element={<LeadPage />} />
            <Route path="/leads/:id/:tab" element={<LeadPage />} />
            <Route path="/follow-ups" element={<FollowUpsPage />} />

            <Route path="/conversations" element={<AllConversationsPage />} />
            <Route path="/conversations/telegram" element={<ConversationsPage />} />
            <Route path="/conversations/email" element={<EmailInboxPage />} />
            <Route path="/conversations/instagram" element={<InstagramInboxPage />} />

            <Route path="/casinos" element={<CasinosPage />} />
            <Route path="/casinos/:id" element={<CasinoPage />} />
            <Route path="/casinos/:id/:tab" element={<CasinoPage />} />
            <Route path="/segments" element={<SegmentsPage />} />
            <Route path="/segments/new" element={<SegmentEditorPage />} />
            <Route path="/segments/:id" element={<SegmentEditorPage />} />

            <Route path="/messaging" element={<MessagingPage />} />
            <Route path="/messaging/messages" element={<MessagesPage />} />
            <Route path="/messaging/messages/new" element={<ComposePage />} />
            <Route path="/messaging/messages/:id" element={<MessagePage />} />
            <Route path="/messaging/messages/:id/edit" element={<ComposePage />} />
            <Route path="/messaging/messages/:id/review" element={<ReviewPage />} />
            <Route path="/messaging/triggers" element={<TriggersPage />} />
            <Route path="/messaging/triggers/new" element={<TriggerEditorPage />} />
            <Route path="/messaging/triggers/:id" element={<TriggerEditorPage />} />
            <Route path="/messaging/templates" element={<TemplatesPage />} />
            <Route path="/messaging/templates/new" element={<TemplateEditorPage />} />
            <Route path="/messaging/templates/:id" element={<TemplateEditorPage />} />

            <Route path="/knowledge" element={<ArticlesPage />} />
            <Route path="/knowledge/answers" element={<AnswersPage />} />
            <Route path="/knowledge/new" element={<ArticleEditorPage />} />
            <Route path="/knowledge/:id" element={<ArticleEditorPage />} />

            {showV2 && (
              <>
                <Route path="/calls" element={<CallsPage />} />
                <Route path="/calls/:id" element={<CallPage />} />
              </>
            )}

            <Route path="/reports" element={<SourcesReportPage />} />
            <Route path="/reports/funnel" element={<FunnelReportPage />} />
            <Route path="/reports/campaigns" element={<CampaignsReportPage />} />

            <Route path="/settings" element={<Navigate to="/settings/staff" replace />} />
            <Route path="/settings/staff" element={<StaffPage />} />
            <Route path="/settings/telegram" element={<TelegramSettingsPage />} />
            <Route path="/settings/channels" element={<ChannelsSettingsPage />} />
            <Route path="/settings/sync" element={<SyncSettingsPage />} />
            <Route path="/settings/assistant" element={<AssistantSettingsPage />} />
            <Route path="/hostess" element={<HostessPage />} />
            <Route path="/staff" element={<Navigate to="/settings/staff" replace />} />

            <Route path="*" element={<Navigate to={SECTIONS[0].path} replace />} />
          </Route>
        </Routes>
      </SignInGate>
    </IdentityProvider>
  </BrowserRouter>
);

export default App;
